import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../hooks/useProjects';
import { Project } from '../types/project';
import { ArrowRight, MapPin, Calendar } from 'lucide-react';

function ProjectCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const coverImage = project.images?.[0]?.imageUrl;

  return (
    <Link
      to={`/projects/${project.id}`}
      className="group block bg-white overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={coverImage || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c'}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        {project.category && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-xs tracking-[0.15em] uppercase text-neutral-700">
            {t(`projects.categories.${project.category}`)}
          </div>
        )}
      </div>
      <div className="p-8">
        <h3 className="text-xl tracking-[0.15em] uppercase mb-3 group-hover:text-neutral-600 transition-colors">
          {project.title}
        </h3>
        <p className="text-neutral-500 text-sm tracking-wide line-clamp-2 mb-4">
          {project.description}
        </p>
        <div className="flex items-center gap-4 text-sm text-neutral-400 mb-4">
          {project.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {project.location}
            </span>
          )}
          {project.year && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {project.year}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm tracking-wide text-neutral-900">
          <span>{t('projects.viewProject')}</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export function ProjectsPage() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const { data, isLoading, error } = useProjects(0, 100, true);

  const categories = ['all', 'Residential', 'Commercial', 'Hospitality', 'Interior', 'Exterior', 'Landscape'];

  const filteredProjects = data?.content?.filter(
    (project) => selectedCategory === 'all' || project.category === selectedCategory
  ) || [];

  return (
    <div className="min-h-screen pt-32 pb-16 px-6 lg:px-16 max-w-[1600px] mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mb-16"
      >
        <div className="mb-8">
          <h1 className="text-5xl md:text-7xl tracking-[0.15em] uppercase font-light mb-4">
            {t('projects.title')}
          </h1>
          <p className="text-neutral-600 tracking-wide">
            {filteredProjects.length} {t('projects.projectCount')}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 uppercase tracking-[0.2em] text-sm transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-neutral-900 text-white shadow-lg'
                  : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
              }`}
            >
              {category === 'all' ? t('gallery.all') : t(`projects.categories.${category}`)}
            </button>
          ))}
        </div>

        <div className="h-px bg-gradient-to-r from-neutral-900 via-neutral-300 to-transparent" />
      </motion.div>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-32">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-neutral-200 border-t-neutral-900 rounded-full mx-auto"
          />
          <p className="mt-6 text-neutral-500 tracking-wide">{t('common.loading')}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-center py-32">
          <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 p-8 rounded">
            <h3 className="text-2xl tracking-wide mb-4 text-red-800">{t('gallery.backendError')}</h3>
            <p className="text-red-600">Cannot connect to the server. Please ensure your backend is running.</p>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      {!isLoading && !error && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <ProjectCard project={project} />
              </motion.div>
            ))}
          </motion.div>

          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-32"
            >
              <p className="text-2xl text-neutral-400 tracking-wide">{t('gallery.noResults')}</p>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
